import { ReactElement, cloneElement, isValidElement, useMemo, useRef } from 'react'
import { useRollingAnimation } from '../../hooks/useRollingAnimation'

type RollingAnimationProps<T> = {
  value: T
  isEqual?: (a: T, b: T) => boolean
  children: ReactElement<{ value: T; className?: string }>
  className?: string
}

/**
 * Generic rolling animation component that handles all animation logic internally.
 * Accepts a single child component that receives a `value` prop to render.
 * 
 * The component automatically handles:
 * - Roll-out animation for previous value
 * - Roll-in animation for current value
 * - Placeholder to maintain height during animation
 * - Conditional rendering based on animation state
 * 
 * @example
 * <RollingAnimation value={someValue}>
 *   <YourComponent value={value} />
 * </RollingAnimation>
 */
export const RollingAnimation = <T,>({ value, isEqual, children, className = '' }: RollingAnimationProps<T>) => {
  const { currentValue, previousValue, isAnimating } = useRollingAnimation({
    value,
    isEqual
  })

  if (!isValidElement(children)) {
    throw new Error('RollingAnimation: children must be a valid React element')
  }

  // Extract base className from children props - memoized to avoid recalculation
  const baseClassName = useMemo(() => {
    return (children.props as { className?: string }).className || ''
  }, [children.props])

  // Pre-compute className strings to avoid string concatenation in render
  const animationClasses = useMemo(() => {
    const base = baseClassName
    return {
      rollOut: base ? `${base} animate-roll-out absolute top-0 left-0 right-0 bottom-0 w-full`.trim() : 'animate-roll-out absolute top-0 left-0 right-0 bottom-0 w-full',
      rollIn: base ? `${base} animate-roll-in absolute top-0 left-0 right-0 bottom-0 w-full`.trim() : 'animate-roll-in absolute top-0 left-0 right-0 bottom-0 w-full',
      placeholder: base ? `${base} opacity-0 pointer-events-none w-full`.trim() : 'opacity-0 pointer-events-none w-full'
    }
  }, [baseClassName])

  // Memoize cloned elements to avoid unnecessary re-renders
  // Only recalculate when animation state or values actually change
  const animatedElements = useMemo(() => {
    if (!isAnimating) {
      return cloneElement(children, { value: currentValue } as { value: T })
    }

    return (
      <>
        {/* Previous value rolling out */}
        {cloneElement(children, {
          value: previousValue,
          className: animationClasses.rollOut
        } as { value: T; className?: string })}
        {/* Current value rolling in */}
        {cloneElement(children, {
          value: currentValue,
          className: animationClasses.rollIn
        } as { value: T; className?: string })}
        {/* Invisible placeholder to maintain height */}
        {cloneElement(children, {
          value: currentValue,
          className: animationClasses.placeholder
        } as { value: T; className?: string })}
      </>
    )
  }, [isAnimating, currentValue, previousValue, children, animationClasses])

  return <div className={`relative ${className}`.trim()}>{animatedElements}</div>
}
